import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LABEL_PRESETS, getPreset, labelLayout, labelPrintCss } from "@/lib/labels";
import { Loader2, Printer, ArrowLeft, Theater, CheckSquare, Square } from "lucide-react";

export default function AssetTags() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [assets, setAssets] = useState(null);
  const [selected, setSelected] = useState({});
  const [venue, setVenue] = useState("Plantagenet Hall");
  const [sizeId, setSizeId] = useState("std");
  const [cw, setCw] = useState(60);
  const [ch, setCh] = useState(30);

  useEffect(() => {
    api.listAssets().then((list) => {
      setAssets(list);
      const preset = params.get("ids");
      if (preset) {
        const ids = preset.split(",");
        setSelected(Object.fromEntries(ids.map((i) => [i, true])));
      } else {
        setSelected(Object.fromEntries(list.map((a) => [a.id, true])));
      }
    }).catch(() => setAssets([]));
  }, [params]);

  const chosen = useMemo(() => (assets || []).filter((a) => selected[a.id]), [assets, selected]);
  const allSelected = assets && chosen.length === assets.length;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (!assets) return;
    setSelected(allSelected ? {} : Object.fromEntries(assets.map((a) => [a.id, true])));
  };

  if (assets === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const preset = getPreset(sizeId);
  const dimW = sizeId === "custom" ? Number(cw) : preset.w;
  const dimH = sizeId === "custom" ? Number(ch) : preset.h;
  const sized = dimW > 0 && dimH > 0;
  const L = labelLayout(dimW, dimH);
  const cardStyle = sized ? { width: `${dimW}mm`, height: `${dimH}mm` } : {};
  return (
    <div className="relative z-10 min-h-screen report-root">
      {sized && <style dangerouslySetInnerHTML={{ __html: labelPrintCss(dimW, dimH) }} />}
      {/* Toolbar */}
      <div className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-xl no-print sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => nav("/assets")} className="gap-2"><ArrowLeft className="h-4 w-4" /> Assets</Button>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          <Select value={sizeId} onValueChange={setSizeId}>
            <SelectTrigger data-testid="label-size" className="h-9 w-56 bg-secondary/40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{LABEL_PRESETS.map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.label}</SelectItem>)}</SelectContent>
          </Select>
          {sizeId === "custom" && (
            <div className="flex items-center gap-1">
              <Input type="number" value={cw} onChange={(e) => setCw(e.target.value)} className="h-9 w-16 bg-secondary/40 text-xs" data-testid="label-w" />
              <span className="text-xs text-muted-foreground">×</span>
              <Input type="number" value={ch} onChange={(e) => setCh(e.target.value)} className="h-9 w-16 bg-secondary/40 text-xs" data-testid="label-h" />
              <span className="text-xs text-muted-foreground">mm</span>
            </div>
          )}
          <Input value={venue} onChange={(e) => setVenue(e.target.value)} className="h-9 w-40 bg-secondary/40" placeholder="Venue name" data-testid="tags-venue" />
          <Button variant="outline" onClick={toggleAll} className="gap-2 tap-scale">{allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />} {allSelected ? "None" : "All"}</Button>
          <Button data-testid="print-tags-btn" onClick={() => window.print()} disabled={chosen.length === 0} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90"><Printer className="h-4 w-4" /> Print {chosen.length}</Button>
        </div>
      </div>

      {/* Selector (screen only) */}
      <div className="mx-auto max-w-5xl px-4 py-5 no-print sm:px-8">
        <h1 className="mb-1 text-xl font-bold tracking-tight sm:text-2xl">Asset tags</h1>
        <p className="mb-4 text-sm text-muted-foreground">Select assets to include, then print a label sheet. Each tag has a scannable QR that opens the asset.</p>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {assets.map((a) => (
            <label key={a.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border px-3 py-2 tap-scale hover:bg-secondary/50">
              <Checkbox checked={!!selected[a.id]} onCheckedChange={() => toggle(a.id)} className="data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
              <span className="min-w-0 flex-1 truncate text-sm">{a.asset_code} · {a.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Printable label sheet */}
      <div className="mx-auto max-w-5xl px-4 pb-10 sm:px-8">
        <div className={`tag-sheet gap-3 ${sized ? "flex flex-wrap" : "grid grid-cols-2 sm:grid-cols-3"}`}>
          {chosen.map((a) => (
            <div key={a.id} style={{ ...cardStyle, padding: `${L.pad}px`, gap: `${L.gap}px` }} className="tag-card print-avoid-break flex rounded-xl border border-border bg-card">
              <div className="shrink-0 rounded-md bg-white" style={{ padding: L.tier === "tiny" ? 2 : 6 }}><QRCodeSVG value={`${window.location.origin}/assets/${a.id}`} size={L.qr} /></div>
              <div className="flex min-w-0 flex-col justify-between">
                <div>
                  {L.showAccent && <div className="flex items-center gap-1 font-semibold uppercase tracking-wider text-primary print-accent" style={{ fontSize: L.accent }}><Theater className="h-3 w-3" /> {venue}</div>}
                  <div className="truncate font-bold leading-tight text-foreground" style={{ fontSize: L.name }}>{a.name}</div>
                  {L.showSecondary && <div className="truncate text-muted-foreground" style={{ fontSize: L.meta }}>{[a.make, a.model].filter(Boolean).join(" ")}</div>}
                </div>
                <div>
                  <div className="font-mono font-extrabold text-foreground" style={{ fontSize: L.code }}>{a.asset_code}</div>
                  {L.showMeta && <div className="truncate text-muted-foreground" style={{ fontSize: L.meta }}>{a.location || "—"}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
